/**
 * Composição do BSC no front.
 *
 * O backend NÃO expõe `/network/bsc` — os quatro pilares são derivados de
 * `overview` + `evolution` + `rejection-reasons` (ver handoff, seção 7). Campos
 * sem fonte no backend (ex.: cancelamentos) são exibidos como "—".
 */
import {
  EvolutionPoint,
  NetworkOverview,
  Pillar,
  RejectionReason,
} from '../../../../../api/theHive/redeProto/types';
import {
  rejectionReasonLabel,
  THRESHOLDS,
} from '../../../../../constants/redeProto';
import { scoreToClassification } from './classify';
import { EMPTY_VALUE, formatCsat, formatInt, formatPct } from './format';

const clamp = (value: number, min: number, max: number): number =>
  Math.min(max, Math.max(min, value));

export const composeBsc = (
  overview: NetworkOverview | undefined,
  evolution: EvolutionPoint[],
  reasons: RejectionReason[]
): Pillar[] => {
  if (!overview) return [];

  const first = evolution[0];
  const last = evolution[evolution.length - 1];

  const { networkSla, avgCsat, firstReviewApproval } = overview;

  const demandGrowth =
    first && last && first.totalDemands > 0
      ? ((last.totalDemands - first.totalDemands) / first.totalDemands) * 100
      : null;

  const totalRejections = reasons.reduce((acc, r) => acc + r.occurrences, 0);
  const topReason = reasons[0];
  const lastDelays = last ? last.totalDemands - last.deliveredOnTime : null;

  const healthScore = clamp(overview.overallAchievement, 0, 100);
  const growthScore = clamp(50 + (demandGrowth ?? 0), 0, 100);
  const experienceScore = clamp(
    ((avgCsat ?? 0) / 5) * 100 * 0.5 + (firstReviewApproval ?? 0) * 0.5,
    0,
    100
  );
  const resultScore = clamp(networkSla ?? 0, 0, 100);

  const healthAlerts: string[] = [];
  if (overview.regionsBelowGoal > 0) {
    healthAlerts.push(
      `${overview.regionsBelowGoal} região(ões) abaixo da meta`
    );
  }
  if (overview.criticalPoles > 0) {
    healthAlerts.push(`${overview.criticalPoles} polos em estado crítico`);
  }

  const growthAlerts: string[] = [];
  if (demandGrowth != null && demandGrowth < 0) {
    growthAlerts.push('Queda no volume de demandas no período');
  }

  const experienceAlerts: string[] = [];
  if (avgCsat != null && avgCsat < THRESHOLDS.csat.critical) {
    experienceAlerts.push('CSAT médio abaixo do mínimo');
  }
  if (
    firstReviewApproval != null &&
    firstReviewApproval < THRESHOLDS.approval.critical
  ) {
    experienceAlerts.push('Aprovação em 1ª revisão abaixo do mínimo');
  }

  const resultAlerts: string[] = [];
  if (networkSla != null && networkSla < THRESHOLDS.sla.healthy) {
    resultAlerts.push('SLA da rede abaixo do alvo');
  }

  return [
    {
      key: 'health',
      label: 'Saúde',
      score: healthScore,
      classification: scoreToClassification(healthScore),
      metrics: [
        {
          label: 'Cobertura da meta',
          value: formatPct(overview.overallAchievement),
        },
        { label: 'Polos críticos', value: formatInt(overview.criticalPoles) },
        {
          label: 'Regiões abaixo da meta',
          value: formatInt(overview.regionsBelowGoal),
        },
        {
          label: 'Credenciados ativos',
          value: formatInt(overview.totalAccredited),
        },
      ],
      alerts: healthAlerts,
    },
    {
      key: 'growth',
      label: 'Crescimento',
      score: growthScore,
      classification: scoreToClassification(growthScore),
      metrics: [
        {
          label: 'Demandas (último mês)',
          value: last ? formatInt(last.totalDemands) : EMPTY_VALUE,
        },
        {
          label: 'Crescimento de demandas',
          value: demandGrowth == null ? EMPTY_VALUE : formatPct(demandGrowth),
        },
        { label: 'SLA atual', value: formatPct(last?.slaPct ?? null) },
        { label: 'CSAT atual', value: formatCsat(last?.avgCsat ?? null) },
      ],
      alerts: growthAlerts,
    },
    {
      key: 'experience',
      label: 'Experiência',
      score: experienceScore,
      classification: scoreToClassification(experienceScore),
      metrics: [
        { label: 'CSAT médio', value: formatCsat(avgCsat) },
        {
          label: 'Aprovação 1ª revisão',
          value: formatPct(firstReviewApproval),
        },
        { label: 'Rejeições (total)', value: formatInt(totalRejections) },
        {
          label: 'Principal motivo',
          value: topReason
            ? rejectionReasonLabel(topReason.reason)
            : EMPTY_VALUE,
        },
      ],
      alerts: experienceAlerts,
    },
    {
      key: 'result',
      label: 'Resultado',
      score: resultScore,
      classification: scoreToClassification(resultScore),
      metrics: [
        { label: 'SLA da rede', value: formatPct(networkSla) },
        {
          label: 'Entregues no prazo (mês)',
          value: last ? formatInt(last.deliveredOnTime) : EMPTY_VALUE,
        },
        { label: 'Atrasos (mês)', value: formatInt(lastDelays) },
        // Cancelamentos não são modelados no backend.
        { label: 'Cancelamentos (mês)', value: EMPTY_VALUE },
      ],
      alerts: resultAlerts,
    },
  ];
};
